import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import ClientForm from '@/components/Client/ClientForm.vue';
import useAppMixin from '@/mixins/appMixin';
import { useClientStore } from '@/store/client';
import { Tag } from '@/types';
import ApiBodyResponseStatus from '@/types/AppApiResponseStatus';
import Client from '@/types/interfaces/Client';
import { DEFAULT_PAGINATION_PAGE, LANG, LOCALSTORAGE_KEYS, PAGINATION_TABLES_NAMES, PROVIDE_KEYS } from '@/utils/constants';
import localStorage from '@/utils/localStorage';
import isEqual from 'lodash/isEqual';
import { inject, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ClientService from '@/backend/service/ClientService';
import useTablePaginationMixin from '@/mixins/tablePaginationMixin';
import { useI18n } from 'vue-i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleClient',
  setup(__props) {

const { setPageTitle } = useAppMixin();
const { handleUpdatePage } = useTablePaginationMixin();
const { t } = useI18n();

const { showSnackbar }: any = inject(PROVIDE_KEYS.SNACKBAR);

const route = useRoute();
const router = useRouter();
const clientStore = useClientStore();
const inProcess = ref(false);
const qrInProcess = ref(false);
const sendAccessInProcess = ref(false);
const clientId = route.params.clientId;
const modeEdit = clientId != undefined;
const existedClient = ref({} as Client);
const showForm = ref(false);

const loadClient = async () => {
  await clientStore
    .fetchClient(Number(clientId))
    .then(
      (client: Client) => {
        existedClient.value = client;
        showForm.value = true;
      },
      () => {
        router.back();
      }
    )
    .catch(() => {
      router.back();
    });
};

if (modeEdit) {
  setPageTitle('PageTitle.Clients.Edit');
  loadClient();
} else {
  setPageTitle('PageTitle.Clients.Create');
  showForm.value = true;
}

function onRefreshClientQR() {
  qrInProcess.value = true;
  clientStore
    .refreshClientQR(existedClient?.value?.id as number)
    .then(() => {
      window.location.reload();
    })
    .finally(() => {
      qrInProcess.value = false;
    });
}

function onSendAccessClientQR() {
  sendAccessInProcess.value = true;
  const locale = localStorage.get(LANG);
  ClientService.sendAccessToContact(existedClient?.value?.id as number, locale)
    .then(
      () => {
        showSnackbar('ResponseMessage.Client.SendAccess.Success');
      },
      () => {
        showSnackbar('ResponseMessage.Client.SendAccess.Error');
      }
    )
    .finally(() => (sendAccessInProcess.value = false));
}

function onSubmitClientForm(data: any) {
  inProcess.value = true;

  const authClientData = localStorage.getObject(LOCALSTORAGE_KEYS.AUTORIZED_USER);
  if (modeEdit) {
    const updClient: Client = {
      id: existedClient?.value.id,
      organizationId: authClientData.orgId
    };
    if (existedClient?.value.firstName != data.formData.firstName) {
      updClient.firstName = data.formData.firstName;
    }
    if (existedClient?.value.lastName != data.formData.lastName) {
      updClient.lastName = data.formData.lastName;
    }
    if (existedClient?.value.email != data.formData.email) {
      updClient.email = data.formData.email;
    }
    if (existedClient?.value.phone != data.formData.phone) {
      updClient.phone = data.formData.phone;
    }
    if (existedClient?.value.status != data.formData.status) {
      updClient.status = data.formData.status;
    }
    let sortedExistedTagIds = existedClient?.value.tags?.map((tag) => (tag as Tag).id).sort();
    let sortedCurrentTagIds = data.formData.tags.sort();
    if (!isEqual(sortedExistedTagIds, sortedCurrentTagIds)) {
      updClient.tagIds = data.formData.tags;
    }else{
      updClient.tagIds = existedClient?.value.tags?.map((tag) => (tag as Tag).id as number).sort();
    }

    if (Object.keys(updClient).length > 2) {
      clientStore
        .updateClient(updClient)
        .then(() => {
          router.back();
          showSnackbar('ResponseMessage.Client.Update.Success');
        })
        .catch((error) => {
          if (error.metadata.data.responseStatus == ApiBodyResponseStatus.DUPLICATE_ROW) {
            const responseMessage = error.metadata.data.data.message;          
            if(responseMessage.includes('email')){
              showSnackbar('ResponseMessage.Client.Create.DuplicateRowFieldError',{field: t('TextField.Email')} );
            }          
          } else {
            showSnackbar('ResponseMessage.Client.Update.Error');
          }
         
        })
        .finally(() => (inProcess.value = false));
    } else {
      router.back();
    }
  } else {
    const client: Client = {
      firstName: data.formData.firstName,
      lastName: data.formData.lastName,
      email: data.formData.email,
      phone: data.formData.phone,
      teleguardId: data.formData.teleguardId,
      tagIds: data.formData.tags
    };
    clientStore
      .createClient(client)
      .then(() => {
        handleUpdatePage(PAGINATION_TABLES_NAMES.CLIENTS, DEFAULT_PAGINATION_PAGE);
        router.back();
        showSnackbar('ResponseMessage.Client.Create.Success');
      })
      .catch((error) => {
        if (error.metadata.data.responseStatus == ApiBodyResponseStatus.BAD_REQUEST) {
          showSnackbar('ResponseMessage.Client.Create.ExceedingLimitError');
        } else if (error.metadata.data.responseStatus == ApiBodyResponseStatus.DUPLICATE_ROW) {
          const responseMessage = error.metadata.data.data.message;          
          if(responseMessage.includes('email')){
            showSnackbar('ResponseMessage.Client.Create.DuplicateRowFieldError',{field: t('TextField.Email')} );
          }else if(responseMessage.includes('teleguardId')){
            showSnackbar('ResponseMessage.Client.Create.DuplicateRowFieldError',{field: t('TextField.TeleguardId')} );
          }          
        } else {
          showSnackbar('ResponseMessage.Client.Create.Error');
        }
      })
      .finally(() => (inProcess.value = false));
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    class: "pa-0",
    fluid: ""
  }, {
    default: _withCtx(() => [
      (showForm.value)
        ? (_openBlock(), _createBlock(ClientForm, {
            key: 0,
            qrInProcess: qrInProcess.value,
            inProcess: inProcess.value,
            sendAccessInProcess: sendAccessInProcess.value,
            formData: existedClient.value,
            edit: modeEdit,
            onSubmitForm: onSubmitClientForm,
            onRefreshQR: onRefreshClientQR,
            onSendAccessClientQR: onSendAccessClientQR
          }, null, 8, ["qrInProcess", "inProcess", "sendAccessInProcess", "formData"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})